import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View, Platform, Linking, ScrollView, useWindowDimensions } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { FontFamily } from '../../../constants/fonts';
import { LocationSummaryCardItem, useLocationSummaryTable } from '../hooks/useLocationSummaryTable';
import { LocationDetailsParams } from '../pages/PerfectTruckAnalytics/perfectTruckKpiApi';
import TableSkeleton from './TableSkeleton';
import { PRIMARY_BLUE, WARNING_RED } from '../constants/colors';
import { setCameFromLocationLink } from '../../../store/slices/perfectTruckAnalyticsUiSlice';
import { setSelectedLocation as setUiSelectedLocation } from '../../../store/slices/uiPreferencesSlice';
import { setSelectedLocation as setAppDataSelectedLocation } from '../../../store/slices/appDataSlice';
import store, { RootState } from '../../../store';
import { useToast } from '../../../components/LhToastContainer';
import { useTheme } from '../../../theme';

const formatNumber = (value: number): string => new Intl.NumberFormat('en-US').format(value);

const getWeekShortLabel = (label: string): string => label;

const getAverageWeekExecution = (item: LocationSummaryCardItem): number => {
  if (item.location.weeks.length === 0) return item.location.ptExecution;
  const total = item.location.weeks.reduce((sum, w) => sum + w.ptExecution, 0);
  return Math.round(total / item.location.weeks.length);
};

interface LocationSummaryMobileCardProps {
  item: LocationSummaryCardItem;
  index: number;
  onToggleExpand: (rowKey: string) => void;
}

const LocationSummaryMobileCard: React.FC<LocationSummaryMobileCardProps> = ({ item, index, onToggleExpand }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const currentLocation = useSelector(
    (state: RootState) => state.uiPreferences.selectedLocation ?? state.appData.selectedLocation,
  );
  const averageExecution = getAverageWeekExecution(item);
  const { width } = useWindowDimensions();
  const isMobileView = width < 768;
  const hasMoreThan4Weeks = item.location.weeks.length > 4;
  const shouldScroll = isMobileView && hasMoreThan4Weeks;

  return (
    <View
      style={[
        styles.card,
        item.expanded && styles.cardExpanded,
        isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default, borderWidth: 1 },
      ]}
    >
      <TouchableOpacity
        activeOpacity={0.85}
        onPress={() => onToggleExpand(item.rowKey)}
        style={[styles.cardHeader, isDark && { backgroundColor: colors.background.surface }]}
        testID={`mobile-location-card-${item.rowKey}`}
      >
        <View style={styles.headerTopRow}>
          <View style={styles.headerLeft}>
            <View style={[styles.orderBadge, isDark && { backgroundColor: colors.background.elevated }]}> 
              <Text style={[styles.orderNumber, isDark && { color: colors.text.primary }]}>{`${index + 1}`}</Text>
            </View>
            <View style={styles.locationBlock}>
              <Text style={[styles.locationName, isDark && { color: colors.text.primary }]}>{item.location.name}</Text>
              <View style={[styles.progressTrack, isDark && { backgroundColor: colors.background.elevated }]}> 
                <View
                  style={[
                    styles.progressFill,
                    { width: `${Math.max(0, Math.min(100, averageExecution))}%` },
                    { backgroundColor: averageExecution < 100 ? WARNING_RED : PRIMARY_BLUE },
                  ]}
                />
              </View>
            </View>
          </View>

          <View style={styles.headerRight}>
            <Text style={[styles.executionValue, { color: averageExecution < 100 ? WARNING_RED : PRIMARY_BLUE }]}>
              {`${averageExecution}%`}
            </Text>
            <MaterialIcons
              color={isDark ? colors.text.primary : '#7A7A7A'}
              name={item.expanded ? 'keyboard-arrow-down' : 'keyboard-arrow-right'}
              size={24}
            />
          </View>
        </View>
      </TouchableOpacity>

      {item.expanded ? (
        <View
          style={[
            styles.expandedSection,
            isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default },
          ]}
        >
          <View style={styles.metricsRow}>
            <Text style={styles.metricInlineText}>
              <Text style={[styles.metricLabel, isDark && { color: colors.text.secondary }]}>Created </Text>
              <Text style={[styles.metricValue, isDark && { color: colors.text.primary }]}>{formatNumber(item.location.created)}</Text>
            </Text>
            <Text style={styles.metricInlineText}>
              <Text style={[styles.metricLabel, isDark && { color: colors.text.secondary }]}>Target </Text>
              <Text style={[styles.metricValue, isDark && { color: colors.text.primary }]}>{formatNumber(item.location.target)}</Text>
            </Text>
            <Text style={styles.metricInlineText}>
              <Text style={[styles.metricLabel, isDark && { color: colors.text.secondary }]}>Approved </Text>
              <Text style={[styles.metricValue, isDark && { color: colors.text.primary }]}>{formatNumber(item.location.approved)}</Text>
            </Text>
            <Text style={styles.metricInlineText}>
              <Text style={[styles.metricLabel, isDark && { color: colors.text.secondary }]}>Pending </Text>
              <Text style={[styles.metricValue, isDark && { color: colors.text.primary }]}>{formatNumber(item.location.pending)}</Text>
            </Text>
            <Text style={styles.metricInlineText}>
              <Text style={[styles.metricLabel, isDark && { color: colors.text.secondary }]}>Declined </Text>
              <Text style={[styles.metricValue, isDark && { color: colors.text.primary }]}>{formatNumber(item.location.declined)}</Text>
            </Text>
          </View>

          {shouldScroll ? (
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={true}
              style={styles.weeksRowScrollContainer}
              contentContainerStyle={styles.weeksRowScroll}
            >
              {item.location.weeks.map((week) => (
                <View key={week.id} style={[styles.weekCard, styles.weekCardScroll, isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }]}>
                  <Text style={[styles.weekLabel, isDark && { color: colors.text.secondary }]}>{getWeekShortLabel(week.weekLabel)}</Text>
                  <Text style={styles.weekValue}>{`${week.ptExecution}%`}</Text>
                </View>
              ))}
            </ScrollView>
          ) : (
            <View style={styles.weeksRow}>
              {item.location.weeks.map((week) => (
                <View key={week.id} style={[styles.weekCard, isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }]}>
                  <Text style={[styles.weekLabel, isDark && { color: colors.text.secondary }]}>{getWeekShortLabel(week.weekLabel)}</Text>
                  <Text style={styles.weekValue}>{`${week.ptExecution}%`}</Text>
                </View>
              ))}
            </View>
          )}

          <TouchableOpacity
            activeOpacity={0.8}
            style={styles.actionLinkWrap}
            onPress={() => {
              // Look up the location in the full locations list (same logic as web LocationLink)
              const state = store.getState();
              const allLocations: any[] = (state as any).appData?.locations ?? [];
              const locationName = item.location.name;

              const match = allLocations.find(
                (loc: any) =>
                  (loc.name ?? loc.displayText ?? loc.locName ?? '')
                    .toLowerCase()
                    .trim() === locationName.toLowerCase().trim(),
              );

              if (match) {
                const locationObj = {
                  ...match,
                  id: String(match.id ?? match.locationId ?? match.locId ?? locationName),
                  displayText: match.name ?? match.displayText ?? match.locName ?? locationName,
                };

                const targetName = String(locationObj.displayText ?? locationObj.name ?? locationName).toLowerCase().trim();
                const currentName = String((currentLocation as any)?.displayText ?? (currentLocation as any)?.name ?? '').toLowerCase().trim();
                const isLocationChange = currentName !== '' ? currentName !== targetName : false;

                store.dispatch(setUiSelectedLocation(locationObj));
                store.dispatch(setAppDataSelectedLocation(locationObj));

                if (isLocationChange) {
                  showToast({
                    header: '',
                    subtext: `Your location has been updated to ${locationObj.displayText ?? locationName}.`,
                    variant: 'info',
                    position: 'top',
                    duration: 0,
                    hideOnBackgroundClick: true,
                    hideOnUserScroll: true,
                    userScrollDismissDelayMs: 3000,
                  });
                }
              }

              // Flag for breadcrumb
              dispatch(setCameFromLocationLink(true));

              // Prefer in-app navigation helper if available
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const nav = (globalThis as any).appNavigate as ((screen: string, params?: any) => void) | undefined;
              if (nav) {
                nav('Perfect Truck');
                return;
              }

              const route = '#Perfect%20Truck';
              if (Platform.OS === 'web' && typeof window !== 'undefined') {
                window.location.hash = route;
              } else {
                Linking.openURL(route).catch(() => {});
              }
            }}
          >
            <Text style={styles.actionLink}>Open Perfect Truck records</Text>
          </TouchableOpacity>
        </View>
      ) : null}
    </View>
  );
};

const LocationSummaryMobileCardList: React.FC<{ filterParams?: LocationDetailsParams }> = ({ filterParams = {} }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const { locationCards, toggleExpanded, loading, currentPage, totalPages, goToPage, total, pageSize } =
    useLocationSummaryTable(filterParams);

  if (loading) {
    return (
      <View style={isDark ? { backgroundColor: colors.background.surface, borderRadius: 8, padding: 4 } : undefined}>
        <TableSkeleton rows={Math.min(6, pageSize)} />
      </View>
    );
  }

  if (!locationCards.length) {
    return (
      <View style={[styles.emptyState, isDark && { backgroundColor: colors.background.surface, borderRadius: 8 }]}>
        <Text style={[styles.emptyStateText, isDark && { color: colors.text.primary }]}>No location summary data available.</Text>
      </View>
    );
  }

  const startRow = total === 0 ? 0 : currentPage * pageSize + 1;
  const endRow = Math.min((currentPage + 1) * pageSize, total);

  return (
    <View style={[styles.container, isDark && { backgroundColor: colors.background.page }]}>
      {locationCards.map((item, index) => (
        <LocationSummaryMobileCard key={item.rowKey} index={currentPage * pageSize + index} item={item} onToggleExpand={toggleExpanded} />
      ))}

      {totalPages > 1 && (
        <View style={styles.paginationBar}>
          <Text style={[styles.paginationInfo, isDark && { color: colors.text.secondary }]}>{`${startRow}–${endRow} of ${total}`}</Text>
          <View style={styles.paginationControls}>
            <TouchableOpacity onPress={() => goToPage(currentPage - 1)} disabled={currentPage === 0} style={[styles.paginationBtn, currentPage === 0 && styles.paginationBtnDisabled]}>
              <MaterialIcons name="chevron-left" size={22} color={currentPage === 0 ? '#6B7280' : isDark ? '#FFFFFF' : '#374151'} />
            </TouchableOpacity>
            <Text style={[styles.paginationLabel, isDark && { color: colors.text.primary }]}>{`${currentPage + 1} / ${totalPages}`}</Text>
            <TouchableOpacity onPress={() => goToPage(currentPage + 1)} disabled={currentPage >= totalPages - 1} style={[styles.paginationBtn, currentPage >= totalPages - 1 && styles.paginationBtnDisabled]}>
              <MaterialIcons name="chevron-right" size={22} color={currentPage >= totalPages - 1 ? '#6B7280' : isDark ? '#FFFFFF' : '#374151'} />
            </TouchableOpacity>
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    rowGap: 10,
  },
  card: {
    backgroundColor: '#F4F7FD',
    borderRadius: 8,
    overflow: 'hidden',
  },
  cardExpanded: {
    backgroundColor: '#F3F7FD',
  },
  cardHeader: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 18,
    paddingTop: 16,
    paddingBottom: 16,
  },
  headerTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  orderBadge: {
    width: 24,
    height: 24,
    borderRadius: 11,
    backgroundColor: '#EFF5FC',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
  },
  orderNumber: {
    fontSize: 13,
    lineHeight: 26,
    fontWeight: '700',
    color: '#616161',
    fontFamily: FontFamily.bold,
  },
  locationBlock: {
    flex: 1,
    marginRight: 12,
  },
  locationName: {
    fontSize: 14,
    lineHeight: 18,
    fontWeight: '600',
    color: '#2F3136',
    fontFamily: FontFamily.bold,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 12,
  },
  executionValue: {
    fontSize: 17,
    lineHeight: 20,
    color: '#C12C01',
    fontFamily: FontFamily.bold,
    marginRight: 8,
    fontWeight: '700',
  },
  progressTrack: {
    marginTop: 5,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#EBF0F8',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 999,
    backgroundColor: '#C12C01',
  },
  expandedSection: {
    paddingHorizontal: 18,
    paddingTop: 15,
    paddingBottom: 10,
    backgroundColor: '#F3F7FD',
    borderWidth: 1,
    borderColor: '#EBEBEB',
  },
  metricsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 2,
  },
  metricInlineText: {
    marginRight: 18,
    marginBottom: 6,
  },
  metricLabel: {
    fontSize: 11,
    lineHeight: 18,
    color: '#616161',
    fontFamily: FontFamily.regular,
  },
  metricValue: {
    fontSize: 12,
    lineHeight: 18,
    color: '#2D2D2D',
    fontFamily: FontFamily.bold,
    fontWeight: '700',
  },
  weeksRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  weeksRowScrollContainer: {
    marginHorizontal: -18,
  },
  weeksRowScroll: {
    flexDirection: 'row',
    gap: 8,
    paddingHorizontal: 18,
  },
  weekCard: {
    width: '23%',
    backgroundColor: '#FFFFFF',
    borderColor: '#EBEBEB',
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: 6,
    paddingVertical: 6,
    paddingHorizontal: 4,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 60,
  },
  weekCardScroll: {
    width: 75,
    minWidth: 75,
    flexShrink: 0,
  },
  weekLabel: {
    fontSize: 13,
    lineHeight: 18,
    color: '#838383',
    fontFamily: FontFamily.bold,
    fontWeight: '600',
  },
  weekValue: {
    marginTop: 1,
    fontSize: 15,
    lineHeight: 22,
    fontWeight: '700',
    color: '#C12C01',
    fontFamily: FontFamily.bold,
  },
  actionLinkWrap: {
    marginTop: 8,
  },
  actionLink: {
    color: '#005CBC',
    fontSize: 12,
    lineHeight: 22,
    fontFamily: FontFamily.bold,
    fontWeight: '600',
  },
  emptyState: {
    paddingVertical: 18,
    alignItems: 'center',
  },
  emptyStateText: {
    color: '#6B7280',
    fontSize: 13,
    fontFamily: FontFamily.medium,
  },
  paginationBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 12,
  },
  paginationInfo: {
    fontSize: 12,
    color: '#6B7280',
    fontFamily: FontFamily.regular,
  },
  paginationControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  paginationBtn: {
    padding: 4,
    borderRadius: 4,
  },
  paginationBtnDisabled: {
    opacity: 0.4,
  },
  paginationLabel: {
    fontSize: 13,
    color: '#374151',
    fontFamily: FontFamily.medium,
    marginHorizontal: 6,
  },
});

export default LocationSummaryMobileCardList;

